import CurrencyInput from "components/CurrencyInput";
import { OptionalDatePicker } from "components/DatePicker/OptionalDatePicker";
import { FormSection } from "components/MainSectionTemplate/types";
import UrlInput from "components/urlinput";
import { YesNoRadioGroupForInPageEditing, YesNoText } from "components/ValidatedFields/YesNoRadioGroup";
import WebsiteLink from "components/Website/WebsiteLink";
import moment from "moment";
import React from "react";
import { getInstructionForInternal, ShowFieldMetaData, ShowFormState } from "shared/utils/fieldsHelper/showFields";
import { formatCurrency } from "utilities/currencyutils";

export const ticketsSection = (
  show: server.dto.Show,
  showInfo: ShowFormState,
  isExternal: boolean,
  fieldMetaData: ShowFieldMetaData,
): FormSection => {
  const buildTicketInstruction = (partial: {
    ticketSalesWebsite?: string;
    doorPrice?: number | null;
    presalePrice?: number | null;
    reducedPrice?: number | null;
    onSaleDate?: Date | null;
    soldOut?: boolean;
  }) =>
    getInstructionForInternal(show.showId, showInfo, {
      tickets: {
        ticketSalesWebsite: showInfo.ticketLink ?? null,
        doorPrice: showInfo.doorPrice,
        presalePrice: showInfo.presalePrice,
        reducedPrice: showInfo.reducedPrice,
        onSaleDate: showInfo.onSaleDate?.toDate() ?? null,
        soldOut: showInfo.soldOut ?? false,
        ...partial,
      }
    });

  const section: FormSection = {
    type: "collapsable",
    key: "showTicketsSection",
    title: RESX.Ticket.Tickets,
    hidden: !site.currentEnvironment.show.enableTickets || isExternal,
    fields: [{
      ...fieldMetaData.ticketLink.fields(),
      key: "ticketLink",
      label: RESX.Ticket.TicketLink,
      render: () => showInfo.ticketLink ? <WebsiteLink website={showInfo.ticketLink} /> : null,
      inPageEdit: {
        initialValue: showInfo.ticketLink ?? "",
        fullWidth: true,
        render: ({ value, onChange, submit }) => (
          <UrlInput
            value={value}
            onValueChange={onChange}
            onEnter={submit}
            validatorKey="ticketLink"
            focusOnMount
            maxLength={serverReference.constants.url.maxUrlLength}
          />
        ),
        type: "confirmOrCancelButton",
        submitChange: (value: string) =>
          window.CoreApi.Show.UpdateForInternalEnvironment(
            buildTicketInstruction({ ticketSalesWebsite: value })
          )
      }
    }, {
      ...fieldMetaData.doorPrice.fields(),
      key: "doorPrice",
      label: RESX.TicketTypes.Door,
      render: () => showInfo.doorPrice != null
        ? <>{formatCurrency(showInfo.doorPrice, show.currency, serverReference.culture.uiLocale)}</>
        : null,
      inPageEdit: {
        initialValue: showInfo.doorPrice,
        render: ({ value, onChange }) => (
          <CurrencyInput
            currency={show.currency}
            value={value}
            onChange={onChange}
            canBeEmpty
            canBeNegative={false}
          />
        ),
        type: "confirmOrCancelButton",
        submitChange: (value: number) =>
          window.CoreApi.Show.UpdateForInternalEnvironment(
            buildTicketInstruction({ doorPrice: value ?? null })
          )
      }
    }, {
      ...fieldMetaData.presalePrice.fields(),
      key: "presalePrice",
      label: RESX.TicketTypes.Presale,
      render: () => showInfo.presalePrice != null
        ? <>{formatCurrency(showInfo.presalePrice, show.currency, serverReference.culture.uiLocale)}</>
        : null,
      inPageEdit: {
        initialValue: showInfo.presalePrice,
        render: ({ value, onChange }) => (
          <CurrencyInput
            currency={show.currency}
            value={value}
            onChange={onChange}
            canBeEmpty
            canBeNegative={false}
          />
        ),
        type: "confirmOrCancelButton",
        submitChange: (value: number) =>
          window.CoreApi.Show.UpdateForInternalEnvironment(
            buildTicketInstruction({ presalePrice: value ?? null })
          )
      }
    }, {
      ...fieldMetaData.reducedPrice.fields(),
      key: "reducedPrice",
      label: RESX.TicketTypes.Reduced,
      render: () => showInfo.reducedPrice != null
        ? <>{formatCurrency(showInfo.reducedPrice, show.currency, serverReference.culture.uiLocale)}</>
        : null,
      inPageEdit: {
        initialValue: showInfo.reducedPrice,
        render: ({ value, onChange }) => (
          <CurrencyInput
            currency={show.currency}
            value={value}
            onChange={onChange}
            canBeEmpty
            canBeNegative={false}
          />
        ),
        type: "confirmOrCancelButton",
        submitChange: (value: number) =>
          window.CoreApi.Show.UpdateForInternalEnvironment(
            buildTicketInstruction({ reducedPrice: value ?? null })
          )
      }
    }, {
      ...fieldMetaData.onSaleDate.fields(),
      key: "onSaleDate",
      label: RESX.Ticket.OnPresale,
      render: () => showInfo.onSaleDate
        ? <>{showInfo.onSaleDate.format("ll")}</>
        : null,
      inPageEdit: {
        initialValue: showInfo.onSaleDate,
        fullWidth: true,
        render: ({ value, onChange }) => (
          <div className="main-section-template-fields-padding">
            <OptionalDatePicker
              validatorKey="onSaleDate"
              minDate={moment(serverReference.constants.appointment.minDate)}
              maxDate={moment(serverReference.constants.appointment.maxDate)}
              value={value}
              onChange={onChange}
            />
          </div>
        ),
        type: "confirmOrCancelButton",
        submitChange: (value: moment.Moment) =>
          window.CoreApi.Show.UpdateForInternalEnvironment(
            buildTicketInstruction({ onSaleDate: value?.toDate() ?? null })
          )
      }
    }, {
      ...fieldMetaData.soldOut.fields(),
      key: "soldOut",
      label: RESX.Ticket.SoldOut,
      render: () => <YesNoText value={showInfo.soldOut} />,
      inPageEdit: {
        initialValue: showInfo.soldOut,
        render: ({ value, onChange }) => (
          <YesNoRadioGroupForInPageEditing
            value={value}
            onChange={(v) => onChange(v, true)}
          />
        ),
        type: "custom",
        submitChange: (value: boolean) =>
          window.CoreApi.Show.UpdateForInternalEnvironment(
            buildTicketInstruction({ soldOut: value })
          )
      }
    }]
  };

  return section;
};
